/**
 * Public analytics API — entry points for page-view and component tracking.
 *
 * Usage:
 *   import { trackPageView, trackComponent, firePageLoadEvent } from "@repo/analytics/analytics";
 */

import { getPageType } from "@repo/shared-utils/common-utils";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import {
  ANALYTICS_DEBUG_DEFAULT_CONFIG,
  ANALYTICS_DEBUG_KEY,
  ANALYTICS_E2E_PAGE_LOAD_TIME_SECONDS_KEY,
  COMPONENT_LOAD_CLICK_TYPE,
  COMPONENT_LOAD_EVENT_TYPE,
  FULLY_LOADED,
  PAGE_TAG_7,
  PAGE_TAG_8,
} from "./constants/analytics.constants";
import {
  buildDataLayerObject,
  composeCleverTapPayload,
  composeDataLayerProps,
  deriveGaEventType,
  dispatchAnalyticsEvent,
  markViewEventTriggered,
  toErrorMessage,
} from "./utils/functions";
import { waitForPageLoadRequestsToSettle } from "./utils/trackPageLoadRequests";
import type {
  ComponentEventInput,
  GaEventType,
  PageViewInput,
  TrackComponentLoadInput,
} from "./types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getDomContentLoadedEndMs = (): number | null => {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
    return null;
  }
  const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  if (!nav) return null;
  const ms = nav.domContentLoadedEventEnd - nav.startTime;
  return Number.isFinite(ms) ? ms : null;
};

// ---------------------------------------------------------------------------
// Page View
// ---------------------------------------------------------------------------

export const trackPageView = async (input?: PageViewInput): Promise<void> => {
  const eventType: GaEventType = "view";
  const eventName = input?.eventName ?? getPageType("tag-7");
  const gaEventType = input?.gaEventType ?? eventType;

  try {
    const dataLayerObject = await buildDataLayerObject({
      event: gaEventType,
      event_type: eventType,
      component: "page",
      page_load_type: input?.pageLoadType,
      page_load_time: input?.pageLoadTime,
    });

    const ctPayload = composeCleverTapPayload(
      {
        eventName,
        componentName: "page",
        moduleName: "",
        eventType,
        cleverTapOverrides: input?.cleverTapOverrides,
        customerId: input?.customerId ?? "NA",
        campaign: input?.campaign ?? "NA",
        sprint: input?.sprint ?? "NA",
        pwaVersion: input?.pwaVersion ?? "NA",
        subsection: input?.subsection ?? "NA",
        segment: input?.segment ?? "NA",
        applicationId: input?.applicationId ?? "NA",
        utmCampaign: input?.utmCampaign ?? "NA",
        utmMedium: input?.utmMedium ?? "NA",
        deviceType: input?.deviceType ?? "NA",
        utmContent: input?.utmContent ?? "NA",
      },
      dataLayerObject
    );

    markViewEventTriggered();

    await dispatchAnalyticsEvent(eventName, ctPayload, dataLayerObject, {
      componentName: "page",
      gaEventType,
      gaOverrides: input?.gaOverrides,
    });
  } catch (error) {
    const errorMessage = toErrorMessage(error);
    logErrorToNewRelic("Failed to track page view", {
      requestedEvent: eventName,
      error: errorMessage,
    });
    console.error("[Analytics] page view dispatch failed", error);
  }
};

// ---------------------------------------------------------------------------
// Component Event (click, view, scroll, impression, etc.)
// ---------------------------------------------------------------------------

export const trackComponent = async (input: ComponentEventInput): Promise<void> => {
  if (!input.componentName) {
    const eventInput = JSON.stringify(input);
    logErrorToNewRelic("Analytics.trackComponent called without componentName", { eventInput });
    console.error("Analytics.trackComponent called without componentName. Event dropped.", input);
    return;
  }

  const eventName = input.eventName ?? getPageType(PAGE_TAG_8);

  try {
    const gaEventType = deriveGaEventType(input.gaEventType, input.eventType);
    const dataLayerProps = composeDataLayerProps(input, gaEventType);
    const dataLayerObject = await buildDataLayerObject(dataLayerProps);
    const ctPayload = composeCleverTapPayload({ ...input, eventName }, dataLayerObject);

    await dispatchAnalyticsEvent(eventName, ctPayload, dataLayerObject, {
      componentName: input.componentName,
      destinationUrl: input.destinationUrl,
      position: input.position,
      gaEventType,
      gaOverrides: input.gaOverrides,
    });
  } catch (error) {
    const errorMessage = toErrorMessage(error);
    logErrorToNewRelic("Failed to track component analytics", {
      eventName,
      componentName: input.componentName,
      error: errorMessage,
    });
    console.error("[Analytics] component dispatch failed", error);
  }
};

// ---------------------------------------------------------------------------
// Component Load (convenience wrapper)
// ---------------------------------------------------------------------------

export const trackComponentLoad = (input: TrackComponentLoadInput): Promise<void> => {
  return trackComponent({
    ...input,
    eventName: input.eventName ?? getPageType(PAGE_TAG_7),
    eventType: input.eventType ?? COMPONENT_LOAD_EVENT_TYPE,
    clickType: input.clickType ?? COMPONENT_LOAD_CLICK_TYPE,
  });
};

// ---------------------------------------------------------------------------
// Page Load Event (call once from _app.tsx / layout.tsx)
// ---------------------------------------------------------------------------

export const firePageLoadEvent = (): void => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  (window as unknown as Record<string, unknown>)[ANALYTICS_DEBUG_KEY] =
    ANALYTICS_DEBUG_DEFAULT_CONFIG;

  const pageViewEventName = getPageType(PAGE_TAG_7);
  let fullyLoadedFired = false;
  const fireFullyLoaded = async () => {
    if (fullyLoadedFired) return;
    fullyLoadedFired = true;

    const settleResult = await waitForPageLoadRequestsToSettle();
    const dclMs = getDomContentLoadedEndMs();
    const endToEndMs = dclMs !== null ? Math.max(dclMs, settleResult.apiBatchEndPerfMs) : null;
    const totalSeconds = endToEndMs !== null ? endToEndMs / 1000 : null;

    if (totalSeconds !== null) {
      (window as unknown as Record<string, unknown>)[ANALYTICS_E2E_PAGE_LOAD_TIME_SECONDS_KEY] =
        totalSeconds;
    }

    trackPageView({
      eventName: pageViewEventName,
      pageLoadType: FULLY_LOADED,
      ...(totalSeconds !== null ? { pageLoadTime: `${totalSeconds.toFixed(2)} SECONDS` } : {}),
    });
  };

  if (document.readyState === "complete") {
    fireFullyLoaded();
  } else {
    window.addEventListener("load", fireFullyLoaded, { once: true });
  }
};
